import { Navigate, Route, Routes } from 'react-router-dom';
import { Layout } from './components/layout';
import {
  ApiKeyProvider,
  AuthProvider,
  ConversationProvider,
  LanguageProvider,
  OrganizationProvider,
  PageProvider,
  ThemeProvider,
} from './contexts';
import { useTokenRefresh } from './hooks';
import './i18n';

// Auth Pages
import ForgotPassword from './pages/auth/ForgotPassword';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ResetPassword from './pages/auth/ResetPassword';
import VerifyEmail from './pages/auth/VerifyEmail';

// Main Pages
import AgentTeams from './pages/AgentTeams';
import AcceptOrganizationInvitation from './pages/AcceptOrganizationInvitation';
import AgentsAndTasks from './pages/AgentsAndTasks';
import Conversations from './pages/Conversations';
import Dashboard from './pages/Dashboard';
import Integrations from './pages/Integrations';
import KnowledgeBase from './pages/KnowledgeBase';
import OrganizationApiKeys from './pages/OrganizationApiKeys';
import OrganizationInvitations from './pages/OrganizationInvitations';
import OrganizationSettings from './pages/OrganizationSettings';
import Profile from './pages/Profile';
import Subscription from './pages/Subscription';
import ToolEmbed from './pages/ToolEmbed';
import Tools from './pages/Tools';
import ToolsByType from './pages/ToolsByType';
import ToolViewer from './pages/ToolViewer';

// Protected Route Component
import ProtectedRoute from './components/ProtectedRoute';

// Componente interno que só renderiza os providers quando autenticado
function AuthenticatedApp() {
  // Hook para renovar token automaticamente
  useTokenRefresh();

  return (
    <PageProvider>
      <OrganizationProvider>
        <ConversationProvider>
          <Routes>
            {/* Protected Routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="conversations" element={<Conversations />} />
              <Route path="knowledge-base" element={<KnowledgeBase />} />
              <Route path="agents-tasks" element={<AgentsAndTasks />} />
              <Route path="agent-teams" element={<AgentTeams />} />
              <Route path="organization/api-keys" element={<OrganizationApiKeys />} />
              <Route
                path="organization/my-apis"
                element={<Navigate to="/organization/api-keys" replace />}
              />
              <Route path="integrations" element={<Integrations />} />
              <Route path="ferramentas" element={<Tools />} />
              <Route path="ferramentas/tipo/:toolType" element={<ToolsByType />} />
              <Route path="ferramentas/:toolId" element={<ToolViewer />} />
              <Route
                path="organization-invitations"
                element={<OrganizationInvitations />}
              />
              <Route path="organization" element={<OrganizationSettings />} />
              <Route path="profile" element={<Profile />} />
              <Route path="subscription" element={<Subscription />} />
            </Route>

            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </ConversationProvider>
      </OrganizationProvider>
    </PageProvider>
  );
}

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <ApiKeyProvider>
            <Routes>
              {/* Auth Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route
                path="/organization-invitations/accept"
                element={<AcceptOrganizationInvitation />}
              />
              <Route
                path="/v1/organization-invitations/accept"
                element={<AcceptOrganizationInvitation />}
              />
              <Route path="/embed/tool/:toolId" element={<ToolEmbed />} />

              {/* All other routes go through authenticated app */}
              <Route path="/*" element={<AuthenticatedApp />} />
            </Routes>
          </ApiKeyProvider>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
